import isArray from 'lodash/isArray';
import { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

export const INFO_BOX_VARIANT = {
  DEFAULT: 'DEFAULT',
  WARNING: 'WARNING',
  DANGER: 'DANGER',
} as const;
export type INFO_BOX_VARIANT =
  typeof INFO_BOX_VARIANT[keyof typeof INFO_BOX_VARIANT];

export const INFO_BOX_MESSAGE_TYPE = {
  DEFAULT: 'DEFAULT',
  DOT: 'DOT',
} as const;
export type INFO_BOX_MESSAGE_TYPE =
  typeof INFO_BOX_MESSAGE_TYPE[keyof typeof INFO_BOX_MESSAGE_TYPE];

interface InfoBoxProps {
  variant?: INFO_BOX_VARIANT;
  title: string;
  message: ReactNode | Array<ReactNode>;
}

const InfoBox: FunctionComponent<InfoBoxProps> = ({
  variant = INFO_BOX_VARIANT.DEFAULT,
  title,
  message,
}) => {
  return (
    <InfoBoxContainer variant={variant}>
      <InfoBoxTitleWrapper>
        <InfoBoxTitle variant={variant}>{title}</InfoBoxTitle>
      </InfoBoxTitleWrapper>
      {isArray(message) ? (
        message.map((text, index) => {
          return (
            <InfoBoxMessage
              type={INFO_BOX_MESSAGE_TYPE.DOT}
              key={`infoBoxMessage-${index}`}
            >
              {text}
            </InfoBoxMessage>
          );
        })
      ) : (
        <InfoBoxMessage type={INFO_BOX_MESSAGE_TYPE.DEFAULT}>
          {message}
        </InfoBoxMessage>
      )}
    </InfoBoxContainer>
  );
};

export default InfoBox;

export const InfoBoxContainer = styled.dl<{ variant: INFO_BOX_VARIANT }>`
  width: 100%;
  margin: 0;
  padding: 32px 24px;
  background-color: ${({ variant, theme }) =>
    variant === INFO_BOX_VARIANT.WARNING
      ? theme.color.blue100
      : variant === INFO_BOX_VARIANT.DANGER
      ? `${theme.color.yellow100}`
      : `${theme.color.gray30}`};
`;

export const InfoBoxTitleWrapper = styled.dt`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 16px;
`;

export const InfoBoxTitle = styled.span<{ variant: INFO_BOX_VARIANT }>`
  margin-top: 2px;
  ${({ theme, variant }) => `
    ${theme.typography.UI16SB};
    color: ${
      variant === INFO_BOX_VARIANT.WARNING
        ? theme.color.blue500
        : variant === INFO_BOX_VARIANT.DANGER
        ? theme.color.yellow700
        : theme.color.gray700
    };
  `};
`;

export const InfoBoxMessage = styled.dd<{ type: INFO_BOX_MESSAGE_TYPE }>`
  position: relative;
  margin: 0;
  margin-bottom: 8px;
  word-break: keep-all;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ theme, type }) => `
    ${theme.typography.UI12M};
    color: ${theme.color.gray400};
    padding-left: ${type === INFO_BOX_MESSAGE_TYPE.DOT ? '18px' : '0'};

    ${
      type === INFO_BOX_MESSAGE_TYPE.DOT
        ? `
      &::before {
        content: '•';
        position: absolute;
        left: 6.5px;
        color: ${theme.color.gray300};
      }`
        : ''
    }
    `};
`;
